import { useEffect, useRef } from 'react';
import { useLocation } from 'wouter';

import { enhancedToast } from '@/shared/utils/enhanced-toast';
import { savePendingCoupon } from '../utils/pending-coupon';

/** Nome do query param que carrega o cupom no deep-link de divulgação. */
const COUPON_QUERY_PARAM = 'cupom';

/**
 * Captura o cupom vindo por deep-link (`/{slug}?cupom=CODIGO`) — TASK-004.
 *
 * Ao entrar na loja por um link com o cupom embutido, lê o código do query
 * param, valida/persiste como cupom pendente da sessão e avisa o cliente com um
 * toast. Em seguida remove o parâmetro da URL — preservando os demais — para que
 * um refresh não reprocesse o cupom nem reexiba o toast. A aplicação efetiva do
 * cupom no pedido é responsabilidade de outra etapa do fluxo.
 */
export function usePendingCouponFromUrl() {
  const [location, navigate] = useLocation();
  const processedRef = useRef(false);

  useEffect(() => {
    // Processa o parâmetro uma única vez por montagem, evitando reentrância.
    if (processedRef.current) return;

    let params: URLSearchParams;
    try {
      params = new URLSearchParams(window.location.search);
    } catch {
      return;
    }

    const rawCode = params.get(COUPON_QUERY_PARAM);
    if (!rawCode) return;

    processedRef.current = true;

    const savedCode = savePendingCoupon(rawCode);

    // Remove o parâmetro do cupom mantendo os demais, para não reprocessar em
    // refreshes e não deixar o código exposto na barra de endereço.
    params.delete(COUPON_QUERY_PARAM);
    const query = params.toString();
    navigate(`${location}${query ? `?${query}` : ''}`, { replace: true });

    if (savedCode) {
      enhancedToast.success('Cupom recebido', {
        description: `O cupom ${savedCode} será aplicado no seu pedido.`,
      });
    }
  }, [location, navigate]);
}
